import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";

import { Backdrop, Box, CircularProgress, useTheme } from "@mui/material";
import { tokens } from "../../theme";
// import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
// import Header from "../../components/Header";
import StatBox from "../../components/StatBox";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import ContactsOutlinedIcon from "@mui/icons-material/ContactsOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import assignmentApi from "../../apis/assignmentApi";
import firmApi from "../../apis/firmApi";
import { FIRM_TYPE } from "../../utilities/constants";
import securityApi from "../../apis/securityApi";
import { setLogout } from "../../state";
import useLookupData from "../../hooks/useLookupData";

const Dashboard = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const colors = tokens(theme.palette.mode);
  const [isLoading, setIsLoading] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [auditingFirms, setAuditingFirms] = useState([]);
  const [auditedFirms, setAuditedFirms] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  
  // Use custom hook for lookup data
  useLookupData();
  
  const loggedInUser = useSelector((state) => {
    return state.user;
  });

  const tenantId = useSelector((state) => {
    return state.tenantId;
  });

  const idToken = useSelector((state) => {
    return state.token;
  });

  useEffect(() => {
    // Prevent infinite loop - don't fetch if no user or token or already logging out
    if (!loggedInUser?.email || !idToken || !tenantId || isLoggingOut) {
      setIsLoading(false);
      return;
    }

    const handleAuthError = (source) => {
      if (isLoggingOut) return; // Prevent multiple logout calls
      
      console.log(`Authentication error detected from ${source} - Logging out user`);
      setIsLoggingOut(true);
      setIsLoading(false);
      dispatch(setLogout());
      // Let the app's authentication guard handle the redirect
    };

    async function fetchData() {
      setIsLoading(true);
      
      try {
        const requestHeader = {
          headers: {
            "x-eqip-tenantid": tenantId,
            "Authorization": idToken
          },
        };

        // Handle APIs individually to catch 401 errors properly
        let responseAssignments, responseUsers;
        
        try {
          responseAssignments = await assignmentApi.get(`/assignments/assignto/${loggedInUser.email}`, requestHeader);
        } catch (assignmentError) {
          console.error("AssignmentApi error:", assignmentError);
          
          // Check for various authentication error scenarios
          const isAuthError = assignmentError.response?.status === 401 || 
                             assignmentError.message?.includes('401') ||
                             assignmentError.message?.includes('Unauthorized') ||
                             assignmentError.message?.includes('unauthorized') ||
                             (assignmentError.code === 'ERR_NETWORK' && assignmentError.config?.headers?.Authorization);
          
          if (isAuthError) {
            handleAuthError("assignmentApi");
            return;
          }
          throw assignmentError;
        }
        
        try {
          responseUsers = await securityApi.get(`/users`, requestHeader);
        } catch (securityError) {
          console.error("SecurityApi error:", securityError);
          console.log("SecurityApi error details:", {
            status: securityError.response?.status,
            message: securityError.message,
            code: securityError.code,
            response: securityError.response
          });
          
          // Check for various authentication error scenarios
          const isAuthError = securityError.response?.status === 401 || 
                             securityError.message?.includes('401') ||
                             securityError.message?.includes('Unauthorized') ||
                             securityError.message?.includes('unauthorized') ||
                             (securityError.code === 'ERR_NETWORK' && securityError.config?.headers?.Authorization);
          
          if (isAuthError) {
            handleAuthError("securityApi");
            return;
          }
          throw securityError;
        }

        let responseFirms;
        try {
          responseFirms = await firmApi.get(`/firms`, requestHeader);
        } catch (firmError) {
          console.error("FirmApi error:", firmError);
          
          // Check for various authentication error scenarios
          const isAuthError = firmError.response?.status === 401 || 
                             firmError.message?.includes('401') ||
                             firmError.message?.includes('Unauthorized') ||
                             firmError.message?.includes('unauthorized') ||
                             (firmError.code === 'ERR_NETWORK' && firmError.config?.headers?.Authorization);
          
          if (isAuthError) {
            handleAuthError("firmApi");
            return;
          }
          throw firmError;
        }

        setAssignments(responseAssignments.data.body);
        setAuditingFirms(responseFirms.data.body.filter(firm => firm.firm_type_id === FIRM_TYPE.AUDITING_FIRM));
        setAuditedFirms(responseFirms.data.body.filter(firm => firm.firm_type_id === FIRM_TYPE.FIRM_TO_BE_AUDITED));
        setUsers(responseUsers.data.body);

        // Lookup data is now handled by useLookupData hook
        setIsLoading(false);
      } catch (err) {
        // Handle Error Here
        console.error("Dashboard general error:", err);
        console.log("Dashboard error details:", {
          status: err.response?.status,
          message: err.message,
          code: err.code,
          response: err.response
        });
        
        // Comprehensive authentication error detection as final backup
        const isAuthError = err.response?.status === 401 || 
                           err.message?.includes('401') ||
                           err.message?.includes('Unauthorized') ||
                           err.message?.includes('unauthorized') ||
                           (err.code === 'ERR_NETWORK' && err.config?.headers?.Authorization);
        
        if (isAuthError) {
          handleAuthError("dashboard general catch");
          return;
        }
        
        setIsLoading(false);
      }
    }
    fetchData();
    // eslint-disable-next-line
  }, [loggedInUser?.email, tenantId, idToken, isLoggingOut]);

  // Reset logging out state when user changes (for re-login scenarios)
  useEffect(() => {
    if (isLoggingOut && !loggedInUser?.email) {
      setIsLoggingOut(false);
    }
  }, [loggedInUser?.email, isLoggingOut]);

  return (
    <Box m="20px">
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isLoading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      {/* HEADER
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header title="ieQip" subtitle="Welcome To Your ieQip Dashboard" />

        <Box>
          <Button
            sx={{
              backgroundColor: colors.blueAccent[700],
              color: colors.grey[100],
              fontSize: "14px",
              fontWeight: "bold",
              padding: "10px 20px",
            }}
          >
            <DownloadOutlinedIcon sx={{ mr: "10px" }} />
            Download Reports
          </Button>
        </Box>
      </Box> */}

      {/* GRID & CHARTS */}
      <Box
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        gridAutoRows="140px"
        gap="20px"
      >
        {/* ROW 1 */}
        
        <Box
          gridColumn="span 3"
          backgroundColor={colors.primary[400]}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <StatBox
            title={isLoading ? <CircularProgress size="20px"/> : assignments.length}
            subtitle="Assignments"
            // progress="0.80"
            // increase="+99%"
            icon={
              <ReceiptOutlinedIcon
                sx={{ color: colors.blueAccent[600], fontSize: "26px" }}
              />
            }
          />
        </Box>
        <Box
          gridColumn="span 3"
          backgroundColor={colors.primary[400]}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <StatBox
            title={isLoading ? <CircularProgress size="20px"/> : auditedFirms.length}
            subtitle="Firms Audited"
            // progress="0.50"
            // increase="+99%"
            icon={
              <ContactsOutlinedIcon
                sx={{ color: colors.blueAccent[600], fontSize: "26px" }}
              />
            }
          />
        </Box>
        <Box
          gridColumn="span 3"
          backgroundColor={colors.primary[400]}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <StatBox
            title={isLoading ? <CircularProgress size="20px"/> : auditingFirms.length}
            subtitle="Auditing Firms"
            // progress="0.30"
            // increase="+5%"
            icon={
              <PersonAddIcon
                sx={{ color: colors.blueAccent[600], fontSize: "26px" }}
              />
            }
          />
        </Box>
        <Box
          gridColumn="span 3"
          backgroundColor={colors.primary[400]}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <StatBox
            title={isLoading ? <CircularProgress size="20px"/> : users.length}
            subtitle="Users"
            // progress="0.75"
            // increase="+99%"
            icon={
              <PeopleOutlinedIcon
                sx={{ color: colors.blueAccent[600], fontSize: "26px" }}
              />
            }
          />
        </Box>


        {/* ROW 2 */}
        {/* <Box
          gridColumn="span 4"
          gridRow="span 2"
          backgroundColor={colors.primary[400]}
          overflow="auto"
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            borderBottom={`4px solid ${colors.primary[500]}`}
            colors={colors.grey[100]}
            p="15px"
          >
            <Typography color={colors.grey[100]} variant="h5" fontWeight="600">
              Recent Inspections
            </Typography>
          </Box>
          {mockTransactions.map((transaction, i) => (
            <Box
              key={`${transaction.txId}-${i}`}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              borderBottom={`4px solid ${colors.primary[500]}`}
              p="15px"
            >
              <Box>
                <Typography
                  color={colors.blueAccent[500]}
                  variant="h5"
                  fontWeight="600"
                >
                  {transaction.txId}
                </Typography>
                <Typography color={colors.grey[100]}>
                  {transaction.user}
                </Typography>
              </Box>
              <Box color={colors.grey[100]}>{transaction.date}</Box>
              <Box
                backgroundColor={colors.blueAccent[500]}
                p="5px 10px"
                borderRadius="4px"
              >
                ${transaction.cost}
              </Box>
            </Box>
          ))}
        </Box> */}

        {/* ROW 3 */}
        {/* <Box
          gridColumn="span 4"
          gridRow="span 2"
          backgroundColor={colors.primary[400]}
          p="30px"
        >
          <Typography variant="h5" fontWeight="600">
            Campaign
          </Typography>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            mt="25px"
          >
            <ProgressCircle size="125" />
            <Typography
              variant="h5"
              color={colors.blueAccent[500]}
              sx={{ mt: "15px" }}
            >
              $48,352 revenue generated
            </Typography>
            <Typography>Includes extra misc expenditures and costs</Typography>
          </Box>
        </Box> */}
      </Box>
    </Box>
  );
};

export default Dashboard;
